import '../src/polyfills';

// Normalize styles to be consistent across browsers
import 'normalize.css';
// Some custom global styles
import '../src/base.css';
import '@prose-ui/theme.css';

import PropTypes from 'prop-types';

import { Helmet as HelmetLibrary } from 'react-helmet';

import { Global } from '@emotion/react';

import Script from 'next/script';
import { createEmotionSsrAdvancedApproach } from 'tss-react/next';

import { availableCountries, countriesCode } from 'constants/countries';

import { stringToBool } from 'utils/envParsing';
import getEnv from 'utils/getEnv';
import { initPublicEnvs } from 'utils/initPublicEnvs';

const { augmentDocumentWithEmotionCache, withAppEmotionCache } = createEmotionSsrAdvancedApproach({
  key: 'css',
});
export { augmentDocumentWithEmotionCache };

/**
 * List of envs that will be exposed to the client
 * Inspired from https://remix.run/docs/en/main/guides/envvars#browser-environment-variables
 */
const ENV = initPublicEnvs();

const App = ({ Component, err, pageProps, userGeolocationGuessedCountry }) => {
  const getLayout = Component.getLayout || (page => page);

  const isConsentCookieSet = () => {
    return typeof document !== 'undefined' && document.cookie.indexOf('cconsent') !== -1;
  };

  const isConsentSet = () => {
    return (
      typeof window !== 'undefined' &&
      (userGeolocationGuessedCountry !== countriesCode.CA || isConsentCookieSet()) // We require consent only for canadian customers
    );
  };

  const isPopupEnabled = () => {
    const isPopupDisabled = getEnv('REACT_APP_ETHYCA_DISABLE_POPUP');

    return !stringToBool(isPopupDisabled);
  };

  return (
    <>
      {/* Initializing client envs. SHOULD be synchronous otherwise lots of stuffs won't work */}
      <Script id="prose-env" strategy="beforeInteractive">
        {`
          window.PROSE_ENV=${JSON.stringify(ENV)};
        `}
      </Script>
      {/* User Country - Necessary to load GTM tags */}
      <Script id="user-guessed-country" strategy="afterInteractive">
        {`
          if (typeof window !== 'undefined') {
            window.PROSE_GUESSED_USER_COUNTRY = '${userGeolocationGuessedCountry}';
          }
        `}
      </Script>

      {/* Ethyca detect popup */}
      <Script id="ethyca-detect-popup" strategy="afterInteractive">
        {`
          (function () {
            /*
            ********************************************************************************
            Consent Banner Detection
            ********************************************************************************
            */
            const COOKIE_NAME = 'cconsent';
            const BANNER_ID = COOKIE_NAME + '-bar';
            const BANNER_HIDDEN = 'ccb--hidden';

            /*
             * Invoke an observer to spy on consent banner changes.
             * We need it to reload the page once the consent has been answered by the customer.
             */
            if (${!isConsentSet()}) {
              const consentBannerChangedObserver = new MutationObserver(function (mutationsList, obsvr) {
                  mutationsList.forEach(function (mutation) {
                      if (mutation.target.id === BANNER_ID) {
                        if (mutation.attributeName === 'class' && mutation.target.classList.contains(BANNER_HIDDEN)) {
                          obsvr.disconnect();
                          window.location.reload();
                        }
                      }
                });
              });
              const observeConsentBannerChanges = () => {
                consentBannerChangedObserver.observe(document.getElementById(BANNER_ID), {
                  attributes: true,
                  attributeFilter: ['class'],
                });
              };
              const consentBannerAddedObserver = new MutationObserver(function (mutationList, obsvr) {
                mutationList.forEach(function (mutation) {
                  mutation.addedNodes.forEach(function (addedNode) {
                    if (addedNode.id === BANNER_ID) {
                      obsvr.disconnect();
                      observeConsentBannerChanges();
                    }
                  });
                });
              });
              const observeConsentBannerAdded = () => {
                consentBannerAddedObserver.observe(document.getElementsByTagName('body')[0], {
                  childList: true,
                });
              }

              observeConsentBannerAdded();
            }

            /*
            ********************************************************************************
            Default Consent Value Displayed
            ********************************************************************************
            */

            if (typeof window !== 'undefined' && ${!isConsentSet()}) {
              /*
               * Set wanted and checked properties of cookie categories to true by default
               */
              var changeDefaultConsent = categories => {
                var newCats = {};
                for (var [cookieCat, settings] of Object.entries(categories)) {
                  newCats[cookieCat] = Object.assign({}, settings, { wanted: true, checked: true });
                }
                return newCats;
              };

              /*
               * Set a Proxy on CookieConsent object and its init property.
               * Proxy the init call to change default cookie categories consent values displayed.
               * The user can still change those values, those are only the defaults applied in case
               * they don't change its.
               */
              window.CookieConsent = new Proxy(window.CookieConsent || {}, {
                /* property access should occurs as usual on the original CookieConsent object */
                get: function() {
                  return Reflect.get.apply(null, arguments);
                },
                /* detects when init property is written in order to be able to proxy it.
                 * Other props set are reflected on the original object.
                */
                set: function(target, prop, value) {
                  if (prop === 'init') {
                    target[prop] = new Proxy(value, {
                      /* Proxies init calls to replace default consent values displayed. */
                      apply: function(tgt, _thisArg, argumentsList) {
                        return tgt(
                          Object.assign(
                            {},
                            argumentsList[0],
                            { categories: changeDefaultConsent(argumentsList[0].categories) }
                          )
                        );
                      },
                    });
                    return true;
                  }
                  return Reflect.set.apply(null, arguments);
                },
              });
            }
          })();
        `}
      </Script>

      {/* Ethyca consent popup */}
      <Script id="init-ethyca-consent-popup" strategy="afterInteractive">
        {`
          if (typeof window !== 'undefined' && ${
            isPopupEnabled() && userGeolocationGuessedCountry === countriesCode.CA
          }) {
            (function (d, s) {
              var f = d.getElementsByTagName(s)[0];
              var j = d.createElement(s);
              j.async = true;
              j.src =
                'https://cdn.ethyca.com/org/qKx6WiWMUn8HyezRAx9vBhY2zDfCJ8bucRBGdRQWzOc%3D/cookie.js';
              f.parentNode.appendChild(j);
            })(document, 'script');
          }
        `}
      </Script>

      <Script id="init-gtm" strategy="afterInteractive">
        {`
          // Check if consent has been asked in relevent cases
          if (window.PROSE_ENV.REACT_APP_SHOULD_LOAD_TRACKING && ${isConsentSet()}) {
            window.dataLayer = window.dataLayer || [];
            (function(d, l) {
              d.push({ originalLocation: l.protocol + '//' + l.hostname + l.pathname + l.search });
            })(window.dataLayer, document.location);
            (function(w, d, s, l, i) {
              w[l] = w[l] || [];
              w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
              var f = d.getElementsByTagName(s)[0],
                j = d.createElement(s),
                dl = l != 'dataLayer' ? '&l=' + l : '';
              j.async = true;
              j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
              f.parentNode.insertBefore(j, f);
            })(window, document, 'script', 'dataLayer', '${getEnv('REACT_APP_GTM_ID')}');
          }
        `}
      </Script>
      <Script id="init-heap" strategy="afterInteractive">
        {`
          // Check if consent has been asked in relevent cases
          if (window.PROSE_ENV.REACT_APP_SHOULD_LOAD_TRACKING && ${isConsentSet()}) {
            (function(heapId) {
              if (!heapId || window.heap) {
                return;
              }
              window.heap = window.heap || [];
              heap.load = function(e, t) {
                (window.heap.appid = e), (window.heap.config = t = t || {});
                var r = t.forceSSL || 'https:' === document.location.protocol,
                  a = document.createElement('script');
                (a.type = 'text/javascript'),
                  (a.async = !0),
                  (a.src = (r ? 'https:' : 'http:') + '//cdn.heapanalytics.com/js/heap-' + e + '.js');
                var n = document.getElementsByTagName('script')[0];
                n.parentNode.insertBefore(a, n);
                for (
                  var o = function(e) {
                      return function() {
                        heap.push([e].concat(Array.prototype.slice.call(arguments, 0)));
                      };
                    },
                    p = [
                      'addEventProperties',
                      'addUserProperties',
                      'clearEventProperties',
                      'identify',
                      'resetIdentity',
                      'removeEventProperty',
                      'setEventProperties',
                      'track',
                      'unsetEventProperty',
                    ],
                    c = 0;
                  c < p.length;
                  c++
                )
                  heap[p[c]] = o(p[c]);
              };

              heap.load(heapId);
            })(${getEnv('REACT_APP_HEAP_ID')});
          }
        `}
      </Script>

      {/* Friendbuy (Refer a Friend) integration */}
      <Script id="init-friendbuy" strategy="afterInteractive">
        {`
      window["friendbuyAPI"] = friendbuyAPI = window["friendbuyAPI"] || [];

      // registers your merchant using your merchant ID found in the
      // retailer app https://retailer.friendbuy.io/settings/general
      friendbuyAPI.merchantId = "${getEnv('REACT_APP_FRIENDBUY_MERCHANT_ID')}";
      friendbuyAPI.push(["merchant", friendbuyAPI.merchantId]);

      // load the merchant SDK and your campaigns
      (function(f, r, n, d, b, u, y) {
        while ((u = n.shift())) {
          (b = f.createElement(r)), (y = f.getElementsByTagName(r)[0]);
          b.async = 1;
          b.src = u;
          y.parentNode.insertBefore(b, y);
        }
      })(document, "script", [
        "https://static.fbot.me/friendbuy.js",
        "https://campaign.fbot.me/" + friendbuyAPI.merchantId + "/campaigns.js",
      ]);
    `}
      </Script>

      {/* Paypal - Load client component */}
      <Script
        id="init-paypal-client"
        src="https://js.braintreegateway.com/web/3.97.2/js/client.min.js"
        strategy="afterInteractive"
      />
      {/* Paypal - Load checkout component */}
      <Script
        id="init-paypal-checkout"
        src="https://js.braintreegateway.com/web/3.97.2/js/paypal-checkout.min.js"
        strategy="afterInteractive"
      />

      <Global />

      {getLayout(
        <Component
          err={err}
          userGeolocationGuessedCountry={userGeolocationGuessedCountry}
          {...pageProps}
        />
      )}

      <noscript>
        <p>
          Prose.com requires JavaScript in order to run properly. Please enable JavaScript in your
          browser settings.
        </p>
        <p>
          If you are attempting to access this site with an assistive device, please reach out to{' '}
          <a href="mailto:hello@prosehair.com">hello@prosehair.com</a> for assistance.
        </p>
      </noscript>
    </>
  );
};

/**
 * Method to access data required for every page.
 * WARNING: Disables ability to perform automatic static optimization,
 * causing every page to be server-side rendered.
 */
App.getInitialProps = async appContext => {
  /** geolocation override */
  const devOverrideGeolocationGuessedCountry =
    (getEnv('NODE_ENV') === 'developement' && getEnv('PROSE_GUESSED_USER_COUNTRY_OVERRIDE')) ||
    null;

  const userGeoRegion = appContext?.ctx?.req?.headers?.['x-client-geo-region'];
  /**
   * We only care about countries where Prose is selling its products.
   * By default, we consider the user to be geolocated in the US.
   */
  const userGeolocationGuessedCountry =
    userGeoRegion && availableCountries.includes(userGeoRegion)
      ? userGeoRegion
      : devOverrideGeolocationGuessedCountry ?? countriesCode.US;

  let helmet = null;
  if (typeof window === 'undefined') {
    helmet = HelmetLibrary.renderStatic();
  }

  return { userGeolocationGuessedCountry, helmet };
};

App.propTypes = {
  Component: PropTypes.any.isRequired,
  err: PropTypes.any,
  pageProps: PropTypes.any,
  userGeolocationGuessedCountry: PropTypes.string,
};

App.defaultProps = {
  err: null,
  pageProps: null,
  userGeolocationGuessedCountry: null,
};

export default withAppEmotionCache(App);
